import * as THREE from 'three/webgpu';
import { nodeKey } from './cubeSphere';
import type { RootCoord, TerrainShape } from './TerrainShape';
import { TILE_RES, type TileGenerator, type TileJob } from './TileGenerator';

export interface TNode {
  key: number;
  face: number;
  level: number;
  x: number;
  y: number;
  slot: number;
  state: 'none' | 'queued' | 'ready';
  /** CPU copy of the tile's heights (arrives a few frames after the GPU tile). */
  heights: Float32Array | null;
  minH: number;
  maxH: number;
  lastUsed: number;
  /** Unit normal at the node centre, into the rock (on a sphere, the direction from its centre). */
  dir: THREE.Vector3;
  /** The node centre on the reference surface (hollow-local). */
  point: THREE.Vector3;
  /** Approximate half-diagonal on the reference surface, metres. */
  halfDiag: number;
  priority: number;
  /**
   * Its four children, once looked up (children()): the walk asks for them every frame, and
   * the map lookups boxed every key (they don't fit a small integer). Dropped when one of
   * them is forgotten.
   */
  kids: TNode[] | null;
  /** The node whose children() listed this one (set for every node the walk reaches). */
  parent: TNode | null;
  /** Scratch for passes that take each node once: the pass's stamp (nextStamp()) once taken. */
  mark: number;
}

export interface DrawNode {
  node: TNode;
  morphStart: number;
  morphEnd: number;
  /** How near the camera the node may come (m): the distance to its bounding sphere. */
  dist: number;
}

const _rc: RootCoord = { root: 0, u: 0, v: 0 };
let stamps = 0;

/** A new stamp for a pass over nodes that marks each one it takes (TNode.mark). */
export function nextStamp(): number {
  return ++stamps;
}
/** Frames a node may sit idle, holding nothing, before it is forgotten. */
const FORGET_AFTER = 600;
/**
 * The smallest split distance, in node sizes, that `detail` may bring. Neighbours could
 * differ by two levels below about 0.6 (the bounding spheres are generous); keep a margin.
 */
const MIN_SPLIT_FACTOR = 1.2;

/**
 * CDLOD quadtree over the root tiles of a hollow's surface (a sphere's six cube faces, a
 * torus's grid of patches). Selection is by distance ranges that double per level
 * (derived from a target on-screen quad size), so neighbours never differ by more than
 * one level and morphing hides every transition. Tiles are generated on demand on the
 * GPU and recycled LRU.
 */
export class TerrainQuadtree {
  readonly nodes = new Map<number, TNode>();
  readonly roots: TNode[] = [];
  /** This frame's nodes to draw (after select()). Entries are reused next frame: don't keep them. */
  readonly drawList: DrawNode[] = [];
  private drawCount = 0;
  private readonly drawPool: DrawNode[] = [];
  private readonly freeSlots: number[] = [];
  /** The node holding each atlas slot (null while the slot is free). */
  private readonly slotOwner: (TNode | null)[];
  private readonly queue = new Set<TNode>();
  private frame = 0;
  private generating = 0;
  /** Bumped by reset(): batches still on the GPU then are ignored when they land. */
  private epoch = 0;
  maxLevel: number;
  /** Distance factor: a node splits when the camera is within k × its size. */
  splitFactor = 5;
  /**
   * Terrain detail (0–1), scaling every split distance. It drops while the tiles the view
   * needs don't fit in the atlas and creeps back once there is room, so a huge hollow at
   * a low quality gets slightly coarser terrain instead of never finishing the ground near
   * the camera.
   */
  detail = 1;
  private coarsenedAt = Number.NEGATIVE_INFINITY;
  private walked = 0;
  private pendingSplits = 0;
  /** Rough bound for heights of tiles we haven't read back yet. */
  heightBound: number;
  /** `load`: the slots this frame's view needs, over the atlas size. */
  stats = { drawn: 0, resident: 0, queued: 0, generatedTotal: 0, load: 0 };

  constructor(
    readonly gen: TileGenerator,
    readonly shape: TerrainShape,
    heightBound: number,
  ) {
    this.heightBound = heightBound;
    // Finest level: ~1 m quads (32 quads per node).
    this.maxLevel = Math.max(1, Math.round(Math.log2(shape.rootArc / 36)));
    for (let s = gen.maxSlots - 1; s >= 0; s--) this.freeSlots.push(s);
    this.slotOwner = new Array(gen.maxSlots).fill(null);
    for (let f = 0; f < shape.rootCount; f++) this.roots.push(this.getNode(f, 0, 0, 0));
  }

  get queueSize(): number {
    return this.queue.size + this.generating;
  }

  nodeArc(level: number): number {
    return this.shape.rootArc / 2 ** level;
  }

  splitDistance(level: number): number {
    return this.splitFactor * this.detail * this.nodeArc(level);
  }

  /** The coarsest `detail` allowed with this split factor. */
  get minDetail(): number {
    return Math.min(1, MIN_SPLIT_FACTOR / this.splitFactor);
  }

  getNode(face: number, level: number, x: number, y: number): TNode {
    const key = nodeKey(face, level, x, y);
    let n = this.nodes.get(key);
    if (!n) {
      const size = 2 / 2 ** level;
      const uc = -1 + (x + 0.5) * size;
      const vc = -1 + (y + 0.5) * size;
      n = {
        key,
        face,
        level,
        x,
        y,
        slot: -1,
        state: 'none',
        heights: null,
        minH: -this.heightBound,
        maxH: this.heightBound,
        lastUsed: 0,
        dir: this.shape.normal(face, uc, vc, new THREE.Vector3()),
        point: this.shape.point(face, uc, vc, new THREE.Vector3()),
        // Generous: the cube-sphere warp makes some nodes ~10% larger than average.
        halfDiag: this.nodeArc(level) * 0.9,
        priority: 0,
        kids: null,
        parent: null,
        mark: 0,
      };
      this.nodes.set(key, n);
    }
    return n;
  }

  /** A node's four children (kept on the node; don't change the list). */
  children(n: TNode): TNode[] {
    let kids = n.kids;
    if (!kids) {
      const l = n.level + 1;
      kids = [
        this.getNode(n.face, l, n.x * 2, n.y * 2),
        this.getNode(n.face, l, n.x * 2 + 1, n.y * 2),
        this.getNode(n.face, l, n.x * 2, n.y * 2 + 1),
        this.getNode(n.face, l, n.x * 2 + 1, n.y * 2 + 1),
      ];
      for (const k of kids) k.parent = n;
      n.kids = kids;
    }
    return kids;
  }

  request(n: TNode, priority: number): void {
    n.lastUsed = this.frame;
    if (n.state !== 'none') {
      n.priority = Math.max(n.priority, priority);
      return;
    }
    n.state = 'queued';
    n.priority = priority;
    this.queue.add(n);
  }

  /**
   * Forget every tile, the roots too, and free every atlas slot: the hollow's terrain is
   * dropped (see HollowScene.unload). init() brings the roots back.
   */
  reset(): void {
    this.epoch++;
    for (const n of this.nodes.values()) {
      n.slot = -1;
      n.state = 'none';
      n.heights = null;
      n.minH = -this.heightBound;
      n.maxH = this.heightBound;
      n.priority = 0;
    }
    this.nodes.clear();
    for (const r of this.roots) {
      r.kids = null;
      this.nodes.set(r.key, r);
    }
    this.freeSlots.length = 0;
    for (let s = this.gen.maxSlots - 1; s >= 0; s--) this.freeSlots.push(s);
    this.slotOwner.fill(null);
    this.queue.clear();
    this.drawList.length = 0;
    this.drawCount = 0;
    this.generating = 0;
    this.detail = 1;
    this.coarsenedAt = Number.NEGATIVE_INFINITY;
    this.walked = 0;
    this.pendingSplits = 0;
    this.stats = { drawn: 0, resident: 0, queued: 0, generatedTotal: 0, load: 0 };
  }

  /** Generate every root tile before the first frame. */
  async init(): Promise<void> {
    for (const r of this.roots) this.request(r, 1e9);
    while (this.queue.size > 0) await this.pump(64);
  }

  /**
   * Choose nodes to draw for a camera at `cam` (hollow-local, f64). `frustum` is in
   * camera-relative render space; `centerRel` = hollow centre − camera (f64).
   */
  select(cam: THREE.Vector3, frustum: THREE.Frustum, centerRel: THREE.Vector3): void {
    this.frame++;
    this.drawCount = 0;
    this.walked = 0;
    this.pendingSplits = 0;
    for (const r of this.roots) this.visit(r, cam, frustum, centerRel);
    // Cut to this frame's count, not emptied first: emptied, the list let go of its storage
    // and grew it again every frame.
    this.drawList.length = this.drawCount;
    this.stats.drawn = this.drawCount;
    this.fitDetail();
    if (this.frame % FORGET_AFTER === 0) this.forgetIdleNodes();
  }

  /**
   * Fit the view to the atlas. It needs a slot for every node walked (drawn nodes, their
   * ancestors, and the culled siblings a parent needs before it can split) and for the
   * four children of every split still waiting. Coarsen quickly while that is over 90% of
   * the atlas; refine slowly while it is under 75%, and not right after coarsening.
   */
  private fitDetail(): void {
    const load = (this.walked + 4 * this.pendingSplits) / this.gen.maxSlots;
    this.stats.load = load;
    if (load > 0.9) {
      this.detail = Math.max(this.minDetail, this.detail * 0.985);
      this.coarsenedAt = this.frame;
    } else if (load < 0.75 && this.frame - this.coarsenedAt > 60) {
      this.detail = Math.min(1, this.detail * 1.0025);
    }
  }

  /**
   * Drop nodes that hold nothing and haven't been touched for a while. getNode() makes
   * them again when needed; without this the map grows with every place the camera
   * has been.
   */
  private forgetIdleNodes(): void {
    // (By value: [key, node] entries were an array apiece.)
    for (const n of this.nodes.values()) {
      if (n.slot < 0 && n.state === 'none' && n.level > 0 && n.lastUsed < this.frame - FORGET_AFTER) {
        this.nodes.delete(n.key);
        // Its parent looks its children up again (getNode makes a new one in its place).
        if (n.parent) n.parent.kids = null;
        n.parent = null;
        n.kids = null;
      }
    }
  }

  private visit(n: TNode, cam: THREE.Vector3, frustum: THREE.Frustum, centerRel: THREE.Vector3): void {
    n.lastUsed = this.frame;
    this.walked++;
    // The node's bounding sphere, its test against the frustum and its distance, in plain
    // numbers. Through the shared Vector3 and Sphere methods (megamorphic: every vector in
    // the engine passes through them) each read boxed a number: 7.5 MB/s of garbage, and
    // five times the time.
    const minH = n.minH;
    const maxH = n.maxH;
    const midH = (minH + maxH) / 2;
    const p = n.point;
    const dir = n.dir;
    const cx = p.x - dir.x * midH;
    const cy = p.y - dir.y * midH;
    const cz = p.z - dir.z * midH;
    const r = n.halfDiag + (maxH - minH) / 2 + 2;
    const sx = cx + centerRel.x;
    const sy = cy + centerRel.y;
    const sz = cz + centerRel.z;
    const planes = frustum.planes;
    for (let i = 0; i < 6; i++) {
      const plane = planes[i];
      const pn = plane.normal;
      if (pn.x * sx + pn.y * sy + pn.z * sz + plane.constant < -r) return;
    }
    const dx = cx - cam.x;
    const dy = cy - cam.y;
    const dz = cz - cam.z;
    const dist = Math.max(0, Math.sqrt(dx * dx + dy * dy + dz * dz) - r);
    if (n.level < this.maxLevel && dist < this.splitDistance(n.level)) {
      const kids = this.children(n);
      let ready = true;
      for (const k of kids) {
        if (k.state !== 'ready') {
          ready = false;
          this.request(k, 1000 - k.level * 10 - dist / 1000);
        }
      }
      if (ready) {
        for (const k of kids) this.visit(k, cam, frustum, centerRel);
        return;
      }
      this.pendingSplits++;
    }
    // A node is drawn by its parent's range: fully morphed to the parent's grid
    // before the parent would take over. Where a node meets a finer one, its own morph
    // must not have begun yet, or the two edges no longer meet (the finer side has fully
    // morphed onto it). Such a seam lies at most a node's width and its relief past the
    // parent's split distance, which a window starting at 0.6 didn't clear: a fifth of
    // the way into the morph at many seams. From 0.7 none were in Verdant's views.
    const parentSplit = this.splitDistance(Math.max(0, n.level - 1));
    // Draw entries are reused from frame to frame (they were a thousand new objects a frame).
    const i = this.drawCount++;
    let d = this.drawPool[i];
    if (!d) {
      d = { node: n, morphStart: 0, morphEnd: 0, dist: 0 };
      this.drawPool.push(d);
    }
    d.node = n;
    d.morphStart = parentSplit * 0.7;
    d.morphEnd = parentSplit * 0.95;
    d.dist = dist;
    this.drawList[i] = d;
  }

  /** The drawn node covering face coordinate (u, v) of root `root`, if any (after select()). */
  drawnAt(root: number, u: number, v: number): DrawNode | null {
    for (const d of this.drawList) {
      const n = d.node;
      if (n.face !== root) continue;
      const size = 2 / 2 ** n.level;
      const u0 = -1 + n.x * size;
      const v0 = -1 + n.y * size;
      if (u >= u0 && u <= u0 + size && v >= v0 && v <= v0 + size) return d;
    }
    return null;
  }

  /** Generate queued tiles (highest priority first). */
  async pump(budget: number): Promise<void> {
    // Forget requests that weren't repeated this frame or the last: the camera has moved
    // on. Left queued, they keep the priority they were asked with and take slots from
    // tiles that are wanted now. request() queues them again if they are wanted.
    for (const n of this.queue) {
      if (n.lastUsed < this.frame - 1) {
        this.queue.delete(n);
        n.state = 'none';
      }
    }
    // Several batches may be in flight: each readback copy is queued right after its
    // compute, so later batches can reuse the staging buffer safely.
    if (this.queue.size === 0 || this.generating >= 4) return;
    const jobs = [...this.queue]
      .sort((a, b) => b.priority - a.priority)
      .slice(0, Math.min(budget, this.gen.maxBatch));
    // Free slots first, then the least recently used tiles that may go.
    const victims = this.oldestEvictable(jobs.length - this.freeSlots.length);
    const tiles: TNode[] = [];
    for (const n of jobs) {
      const slot = this.freeSlots.pop() ?? this.evict(victims.pop());
      if (slot < 0) break;
      n.slot = slot;
      this.slotOwner[slot] = n;
      // A tile that was just given a slot is in use: it must not be the next victim,
      // in this batch or the next one.
      n.lastUsed = this.frame;
      tiles.push(n);
      this.queue.delete(n);
    }
    if (tiles.length === 0) return;
    const epoch = this.epoch;
    this.generating++;
    const batch: TileJob[] = tiles.map((n) => ({
      face: n.face,
      level: n.level,
      x: n.x,
      y: n.y,
      slot: n.slot,
    }));
    const promise = this.gen.generate(batch);
    // The GPU tile is valid for rendering as soon as the compute is submitted (and only
    // with a real slot: drawing slot -1 would read other tiles' texels).
    for (const n of tiles) n.state = n.slot >= 0 ? 'ready' : 'none';
    this.stats.generatedTotal += tiles.length;
    try {
      const heights = await promise;
      // Dropped meanwhile (reset): these tiles belong to nobody now.
      if (epoch !== this.epoch) return;
      tiles.forEach((n, i) => {
        if (n.state !== 'ready' || n.slot < 0) return;
        const h = heights[i];
        n.heights = h;
        let lo = Number.POSITIVE_INFINITY;
        let hi = Number.NEGATIVE_INFINITY;
        for (let k = 0; k < h.length; k++) {
          const v = h[k] > 0 ? h[k] : 0;
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
        n.minH = lo;
        n.maxH = hi;
      });
    } finally {
      if (epoch === this.epoch) this.generating--;
    }
    this.stats.queued = this.queue.size;
    this.stats.resident = this.gen.maxSlots - this.freeSlots.length;
  }

  /**
   * The `count` least recently used tiles that may be evicted, oldest last. Only ready
   * tiles qualify, never the roots, and nothing used this frame or the last: a queued
   * tile given a slot earlier in the same batch would otherwise lose it and go out to the
   * GPU as slot -1, then be drawn from other tiles' texels. One pass over the slots.
   */
  private oldestEvictable(count: number): TNode[] {
    const out: TNode[] = []; // newest first
    if (count <= 0) return out;
    for (const n of this.slotOwner) {
      if (n?.state !== 'ready' || n.level === 0 || n.lastUsed >= this.frame - 1) continue;
      if (out.length === count) {
        if (n.lastUsed >= out[0].lastUsed) continue;
        out.shift();
      }
      let i = 0;
      while (i < out.length && out[i].lastUsed >= n.lastUsed) i++;
      out.splice(i, 0, n);
    }
    return out;
  }

  /** Take a tile's slot back (-1 when there is no tile to take it from). */
  private evict(victim: TNode | undefined): number {
    if (!victim) return -1;
    const s = victim.slot;
    victim.slot = -1;
    victim.state = 'none';
    victim.heights = null;
    victim.minH = -this.heightBound;
    victim.maxH = this.heightBound;
    return s;
  }

  /** Height under a hollow-local point (see heightAt). */
  heightAtLocal(p: THREE.Vector3): number {
    this.shape.locate(p, _rc);
    return this.heightAt(_rc.root, _rc.u, _rc.v);
  }

  /**
   * Height (m into the void) at root coordinates, from the finest node with CPU data,
   * bilinear. Water is not clamped here. Returns NaN if nothing is loaded.
   */
  heightAt(face: number, u: number, v: number): number {
    for (let level = this.maxLevel; level >= 0; level--) {
      const n = 2 ** level;
      const x = Math.min(n - 1, Math.max(0, Math.floor(((u + 1) / 2) * n)));
      const y = Math.min(n - 1, Math.max(0, Math.floor(((v + 1) / 2) * n)));
      const node = this.nodes.get(nodeKey(face, level, x, y));
      if (!node?.heights) continue;
      const size = 2 / n;
      const gx = ((u - (-1 + x * size)) / size) * (TILE_RES - 1);
      const gy = ((v - (-1 + y * size)) / size) * (TILE_RES - 1);
      return sampleTile(node.heights, gx, gy);
    }
    return Number.NaN;
  }
}

export function sampleTile(h: Float32Array, gx: number, gy: number): number {
  const x0 = Math.min(TILE_RES - 2, Math.max(0, Math.floor(gx)));
  const y0 = Math.min(TILE_RES - 2, Math.max(0, Math.floor(gy)));
  const fx = Math.min(1, Math.max(0, gx - x0));
  const fy = Math.min(1, Math.max(0, gy - y0));
  const i = y0 * TILE_RES + x0;
  const a = h[i] * (1 - fx) + h[i + 1] * fx;
  const b = h[i + TILE_RES] * (1 - fx) + h[i + TILE_RES + 1] * fx;
  return a * (1 - fy) + b * fy;
}
